/**
 * Utility functions for creating DOM selectors
 * import { 
    createSelector, 
    createPrefixSelector, 
    createContainsSelector,
    createSuffixSelector,
    combineSelectors 
} from './selectorUtils';
 * @module selectorUtils
 */

/**
 * 
 *      const trigger = Modal.Selectors.TRIGGER(this.#modalName);  // '[data-ts-trigger="modalName"]'
 * 
 *      // Get raw values
 *      const rawTrigger = Modal.Selectors.raw.TRIGGER(this.#modalName);  // 'data-ts-trigger="modalName"'
 */
function deepFreeze(obj) {
    Object.keys(obj).forEach(prop => {
        if (typeof obj[prop] === 'object' && obj[prop] !== null) {
            deepFreeze(obj[prop]);
        }
    });
    return Object.freeze(obj);
}

export const createSelectors = (selectors) => {
    // Freeze the input selectors
    const frozenSelectors = deepFreeze({ ...selectors });

    // Create a separate object for the bracketed versions
    const bracketedSelectors = new Proxy({}, {
        get(target, prop) {
            if (prop === 'raw') return frozenSelectors;

            if (typeof frozenSelectors[prop] === 'object') {
                return new Proxy({}, {
                    get(obj, key) {
                        if (key === 'raw') return frozenSelectors[prop];
                        const value = frozenSelectors[prop][key];
                        if (typeof value === 'function') {
                            return (...params) => `[${value(...params)}]`;
                        }
                        return `[${value}]`;
                    }
                });
            }

            const value = frozenSelectors[prop];
            if (typeof value === 'function') {
                return (...params) => `[${value(...params)}]`;
            }
            return `[${value}]`;
        }
    });

    return bracketedSelectors;
}



/**
 * Creates attribute selector
 * createSelector('data-tc-button') = <button data-tc-button>Click me</button>
 * createSelector('data-tc-type', 'primary') = <button data-tc-type="primary">Primary button</button>
 * @param {string} attribute - Attribute name
 * @param {string} [value] - Optional attribute value
 * @returns {string} Attribute selector
 */
export const createSelector = (attribute, value) =>
    value ? `[${attribute}="${value}"]` : `[${attribute}]`;

/**
 * Creates prefix selector for attribute matching
 * createPrefixSelector('data-tc-id', 'tour-') = <div data-tc-id="tour-123">Tour item</div>
 * @param {string} attribute - Attribute name
 * @param {string} prefix - Prefix to match
 * @returns {string} Attribute selector with prefix
 */
export const createPrefixSelector = (attribute, prefix) =>
    `[${attribute}^="${prefix}"]`;

/**
 * Creates contains selector for attribute matching
 * createContainsSelector('data-tc-tags', 'premium') = <div data-tc-tags="premium-tour">Premium tour</div>
 * @param {string} attribute - Attribute name
 * @param {string} value - Value to match
 * @returns {string} Attribute selector with contains
 */
export const createContainsSelector = (attribute, value) =>
    `[${attribute}*="${value}"]`;

/**
 * Creates suffix selector for attribute matching
 * createSuffixSelector('data-tc-price', '-gbp') = <div data-tc-price="100-gpb">£100 Price</div>
 * @param {string} attribute - Attribute name
 * @param {string} suffix - Suffix to match
 * @returns {string} Attribute selector with suffix
 */
export const createSuffixSelector = (attribute, suffix) =>
    `[${attribute}$="${suffix}"]`;

/**
 * Combines multiple selectors, ALL selectors must match
 *  combineSelectors([
            createSelector('data-tc-type', 'tour'),
            createContainsSelector('data-tc-region', 'europe')
        ]) = <div data-tc-type="tour" data-tc-region="europe-west">Match</div>
 * @param {string[]} selectors - Array of selectors to combine
 * @returns {string} Combined selector
 */
export const combineSelectors = (selectors) =>
    selectors.join('');